/**
 * Native platform utilities for Capacitor Android/iOS.
 * Centralizes platform detection, back-button handling, and native integrations.
 */

const getIsNativePlatform = (): boolean => {
  try {
    const cap = (window as any).Capacitor;
    if (!cap) return false;
    // Primary check
    if (typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) return true;
    // Fallback: getPlatform() still works when loading from remote URL
    if (typeof cap.getPlatform === 'function') {
      const platform = cap.getPlatform();
      if (platform === 'android' || platform === 'ios') return true;
    }
    // Final fallback: native bridge property
    if (cap.isNative === true) return true;
    return false;
  } catch {
    return false;
  }
};

/**
 * Initialize native back-button handling for Android.
 * Navigates backward in WebView history before exiting the app.
 * Should be called once at app startup.
 */
export async function initNativeBackButton(): Promise<() => void> {
  if (!getIsNativePlatform()) return () => {};

  try {
    const { App } = await import('@capacitor/app');
    const handle = await App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
    return () => handle.remove();
  } catch (err) {
    console.warn('[NativeUtils] Failed to set up back button handler:', err);
    return () => {};
  }
}

/**
 * Apply native status bar theming.
 * Sets the brand color and light text for the Android status bar.
 */
export async function initStatusBar(): Promise<void> {
  if (!getIsNativePlatform()) return;

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setBackgroundColor({ color: '#083344' });
    await StatusBar.setStyle({ style: Style.Dark }); // Light text on dark background
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch (err) {
    console.warn('[NativeUtils] Failed to configure status bar:', err);
  }
}

/**
 * Initialize all native platform features.
 * Call once at app startup.
 */
export async function initNativePlatform(): Promise<() => void> {
  if (!getIsNativePlatform()) return () => {};

  const backButtonCleanup = await initNativeBackButton();
  await initStatusBar();

  return backButtonCleanup;
}

export { getIsNativePlatform };
